// ntumaDigitalItemsSlice.ts
import { EntityState, createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { APP_STATUS, BASE_URL, TypeActiveEnviroment, TypeAppStatus } from "../../helpers/constants";
import axios, { AxiosError } from "axios";
import { selectAccessToken } from "../auth/authSlice";
import { RootState } from "../../app/store";
import { handleSessionExpiration } from "../../helpers/commons";

// ---------------- TYPES ----------------
export type TypeNtumaDigitalItem = {
    id: number;
    ntuma_account_id: number;
    ref: string;
    title: string;
    description: string;
    image: string | null;
    file: string | null;
    price: number | null;
    original_price: number | null;
    category: string | null;
    type: string;
    active_environment: TypeActiveEnviroment;
    extra_fields: any[];
    created_at: string;
    updated_at: string;
    seller_note: string | null;
    buyer_note: string | null;
    NtumaAccount: {
        account_name: string;
        business_details: string | null;
        companyBranding: {
            id: string;
            ntuma_account_id: number;
            company_logo: string;
            created_at: string;
            updated_at: string;
        };
    };
};

interface NtumaDigitalItemState extends EntityState<TypeNtumaDigitalItem> {
    loadingItemsStatus: TypeAppStatus;
    lastCursor: string | null;
    nextCursor: string | null;
    totalItems: number | null;
    totalPages: number;
    cursorReset: boolean;
}

// ---------------- ADAPTER & INITIAL STATE ----------------
const ntumaDigitalItemsAdapter = createEntityAdapter<TypeNtumaDigitalItem>();

const initialState: NtumaDigitalItemState = ntumaDigitalItemsAdapter.getInitialState({
    loadingItemsStatus: "idle",
    lastCursor: null,
    nextCursor: null,
    totalItems: null,
    totalPages: 0,
    cursorReset: true
});

// ---------------- ASYNC THUNK ----------------
export const fetchNtumaDigitalItems = createAsyncThunk<
    {
        items: TypeNtumaDigitalItem[];
        lastCursor: string | null;
        nextCursor: string | null;
        totalItems: number | null;
        totalPages: number;
    },
    void,
    { state: RootState }
>(
    "ntuma/fetchNtumaDigitalItems",
    async (_, { getState, rejectWithValue, dispatch }) => {
        try {
            const state = getState();
            const auth_token = selectAccessToken(state);
            const nextCursor = state.ntumaDigitalItems.nextCursor; // Type-safe access
            const dataUrl = nextCursor
                ? `/api/donation-link?cursor=${nextCursor}`
                : "/api/donation-link/get_digital_items";

            const response = await axios.get(BASE_URL + dataUrl, {
                headers: { Authorization: 'Bearer ' + auth_token }
            });

            console.log("M-NTUMA-Items", response.data);

            const data = response.data.data;

            return {
                items: data.items as TypeNtumaDigitalItem[],
                lastCursor: data.lastCursor,
                nextCursor: data.nextCursor,
                totalItems: data.totalItems,
                totalPages: data.totalPages
            };
        } catch (error) {
            const err = error as AxiosError;
            handleSessionExpiration(err, dispatch);
            return rejectWithValue("Failed. Something went wrong");
        }
    }
);

// ---------------- SLICE ----------------
const ntumaDigitalItemsSlice = createSlice({
    name: "ntumaDigitalItems",
    initialState,
    reducers: {
        resetCursor(state) {
            state.nextCursor = null;
            state.lastCursor = null;
            state.cursorReset = true;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNtumaDigitalItems.pending, (state) => {
                state.loadingItemsStatus = APP_STATUS.PENDING;
            })
            .addCase(fetchNtumaDigitalItems.fulfilled, (state, action) => {
                state.loadingItemsStatus = APP_STATUS.SUCCESS;
                if (state.cursorReset) {
                    ntumaDigitalItemsAdapter.removeAll(state);
                }
                ntumaDigitalItemsAdapter.addMany(state, action.payload.items);
                state.lastCursor = action.payload.lastCursor;
                state.nextCursor = action.payload.nextCursor;
                state.totalItems = action.payload.totalItems;
                state.totalPages = action.payload.totalPages;
                state.cursorReset = false;
            })
            .addCase(fetchNtumaDigitalItems.rejected, (state) => {
                state.loadingItemsStatus = APP_STATUS.ERROR;
            });
    }
});

// ---------------- EXPORTS ----------------
export const { resetCursor } = ntumaDigitalItemsSlice.actions;

export default ntumaDigitalItemsSlice.reducer;

export const {
    selectAll: selectNtumaDigitalItems,
    selectById: selectNtumaDigitalItemById
} = ntumaDigitalItemsAdapter.getSelectors((state: RootState) => state.ntumaDigitalItems);

export const selectNtumaNextCursor = (state: RootState): string | null => state.ntumaDigitalItems.nextCursor;
